import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";
import api from "@/lib/api";

const TYPE_DOT = {
  expense_pending: "var(--amber)",
  expense_approved: "var(--green)",
  expense_rejected: "var(--red)",
  expense_paid: "var(--cyan)",
  project_assigned: "var(--magenta)",
  system: "var(--text-dim)",
};

const fmtAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    if (!navigator.onLine) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onOnline = () => load();
    window.addEventListener("online", onOnline);
    return () => { clearInterval(t); window.removeEventListener("online", onOnline); };
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const click = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`);
      load();
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center rounded-sm transition-colors hover:bg-white/5"
        style={{ border: "1px solid var(--border-2)" }}
        aria-label="Notificaciones"
      >
        <Bell className="w-[18px] h-[18px] text-slate-300" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold rounded-full font-mono"
            style={{ background: "var(--magenta)", color: "#0c1424", boxShadow: "0 0 12px var(--magenta-glow)" }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-11 w-96 max-w-[calc(100vw-2rem)] panel-strong z-50"
          style={{ maxHeight: "70vh", overflow: "hidden", display: "flex", flexDirection: "column" }}
        >
          <div className="panel-header px-4 py-3 flex items-center justify-between">
            <div>
              <div className="label-mono">// Notificaciones</div>
              <div className="text-sm font-display font-bold text-slate-100 tracking-wide mt-0.5">
                {unread > 0 ? `${unread} sin leer` : "Sin pendientes"}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button data-testid="mark-all-read-btn" onClick={markAll} className="text-[10px] label-mono hover:underline" style={{ color: "var(--cyan)" }}>
                  <Check className="w-3 h-3 inline mr-0.5" /> TODAS
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200 p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: "60vh" }}>
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-slate-500 text-sm">
                No tienes notificaciones todavía.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => click(n)}
                className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors flex gap-3"
                style={{ borderBottom: "1px solid var(--border)", opacity: n.read ? 0.6 : 1 }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                  style={{
                    background: TYPE_DOT[n.type] || "var(--cyan)",
                    boxShadow: n.read ? "none" : `0 0 8px ${TYPE_DOT[n.type] || "var(--cyan)"}`,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-100">{n.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5 leading-snug">{n.message}</div>
                  <div className="label-mono text-[9px] mt-1.5">{fmtAgo(n.created_at)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
